import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import isEqual from "lodash/isEqual";
import { Sort, Row } from "@evergis/ui";
import { PagedFeaturesListDc } from "@evergis/api";

import { TABLE_PAGE_LIMIT } from "./useTableProps";

import { features } from "../../../resources/features";
import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { useLayerCrs } from "../../../hooks/layer/useLayerCrs";
import { useGeometrySelectionConditions } from "../../../hooks/geometrySelection/useGeometrySelectionConditions";
import { getDateFilterConditions } from "../../../ducks/dateFilter/selectors";

import { SetTableValue, TableQueryParams } from "../types";
import { LayerInfo } from "../../../interfaces/resourceInfo";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";
import { useEwktGeometry } from "../../FeatureSelector/hooks/useEwktGeometry";

interface TableDataResponse {
  layerFeatures: PagedFeaturesListDc;
  tableData: SetTableValue;
  tableDataLoading: boolean;
  tableDataError: Error;
  totalRowsCount: number;
  tableDataRefetch: () => Promise<unknown>;
}

// eslint-disable-next-line max-lines-per-function
export const useTableData = (
  layerInfo: LayerInfo,
  currentPage: number,
  queryParams: TableQueryParams,
  sort: Sort,
  isFiltering?: boolean,
): TableDataResponse => {
  const [crs] = useLayerCrs(layerInfo.name);
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const prevQueryParams = useRef(null);
  const { layerDefinition } = layerInfo;
  const geometrySelectionConditions = useGeometrySelectionConditions();
  const dateFilterConditions = useSelector(getDateFilterConditions);
  const ewktGeometry = useEwktGeometry();

  const [tableData, setTableData] = useState<SetTableValue>({
    initial: [],
    current: [],
  });

  const featuresInput = useMemo(() => {
    return (
      layerInfo.name && {
        name: layerInfo.name,
        limit: TABLE_PAGE_LIMIT,
        offset: ((currentPage || 1) - 1) * TABLE_PAGE_LIMIT,
        query: [
          queryParams.query,
          geometrySelectionConditions?.[layerInfo.name],
          !isFiltering ? dateFilterConditions?.[layerInfo.name] : undefined,
        ]
          .filter(Boolean)
          .join(` ${FilterConditionOperation.AND} `),
        sort: Object.entries(sort).map(([key, value]) => {
          return `${value === "desc" ? "-" : ""}${key}`;
        }),
        wkid: crs.wkid,
        withgeom: false,
        ewktGeometry,
      }
    );
  }, [
    layerInfo.name,
    currentPage,
    queryParams.query,
    geometrySelectionConditions,
    isFiltering,
    dateFilterConditions,
    sort,
    crs.wkid,
    ewktGeometry,
  ]);

  const {
    data: featuresList,
    loading: featuresListLoading,
    refetch: featuresListRefetch,
    error,
  } = features.use({
    input: {
      layerName: featuresInput.name,
      params: featuresInput,
    },
    waitInput: true,
    immediate: layerInfo.type !== "RemoteTileService",
  });

  useEffect(() => {
    if (
      prevQueryParams.current !== undefined &&
      !featuresListLoading &&
      !isEqual(queryParams.conditions, prevQueryParams.current?.conditions)
    ) {
      featuresListRefetch();
      prevQueryParams.current = queryParams;
    }
  }, [queryParams.conditions]);

  useEffect(() => {
    if (!featuresList) {
      return;
    }

    const data: Row[] =
      (featuresList.items &&
        featuresList.items.map(feature => {
          const dataAttributes = {};

          if (feature.attributes) {
            for (const attribute in feature.attributes) {
              if (feature.attributes[attribute] !== null && layerDefinition.attributes[attribute]) {
                const { type, stringFormat } = layerDefinition.attributes[attribute];

                dataAttributes[attribute] = formatAttributeValue(type, feature.attributes[attribute], stringFormat);
              }
            }
          }

          return {
            id: feature.id,
            data: dataAttributes,
          };
        })) ||
      [];

    setTableData({
      initial: [...data],
      current: [...data],
    });

    setTotalRowsCount(featuresList.totalCount);
  }, [featuresList]); // eslint-disable-line

  return {
    layerFeatures: featuresList,
    tableData,
    tableDataLoading: featuresListLoading,
    tableDataRefetch: featuresListRefetch,
    tableDataError: error,
    totalRowsCount,
  };
};
